<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="isLoading"
      :options.sync="tableOptions"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, -1],
        'items-per-page-text': 'Filas por página:'
      }"
      class="elevation-0"
      loading-text="Cargando entregas pendientes..."
      no-data-text="No hay mercancía pendiente de entrega."
    >
      <template v-slot:[`item.folio`]="{ item }">
        <a class="font-weight-bold primary--text text-decoration-underline cursor-pointer" @click.prevent="openDetailModal(item)">
          #{{ item.folio }}
        </a>
      </template>

      <template v-slot:[`item.clientName`]="{ item }">
        <span class="font-weight-medium">{{ item.clientName }}</span>
      </template>

      <template v-slot:[`item.totalItemsPending`]="{ item }">
        {{ item.totalItemsPending }} pz.
      </template>

      <template v-slot:[`item.purchaseDate`]="{ item }">
        {{ formatDate(item.purchaseDate) }}
      </template>

      <template v-slot:[`item.statusDescription`]="{ item }">
        <v-chip small dark :color="getItemStatusColor(item.statusId)" class="font-weight-medium">
          {{ item.statusDescription }}
        </v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end">
          <!-- Botón: Imprimir Vale de Resguardo -->
          <v-tooltip top color="grey darken-3">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                class="mr-3"
                v-bind="attrs"
                v-on="on"
                @click="openCollectionTicketPreview(item)"
              >
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </template>
            <span>Imprimir Vale de Resguardo</span>
          </v-tooltip>

          <!-- Botón: Entregar Pedido -->
          <v-btn
            :color="item.statusId === 4 ? 'orange darken-2' : 'grey lighten-3'"
            :dark="item.statusId === 4"
            :disabled="item.statusId !== 4"
            small
            depressed
            class="text-none font-weight-medium"
            @click="goToDispatch(item)"
          >
            <v-icon left small>{{ item.statusId === 4 ? 'mdi-check' : 'mdi-lock' }}</v-icon>
            Entregar
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <delivery-order-details-modal
      v-model="showModal"
      :folio="selectedFolio"
      :store-id="currentStoreId"
    />
  </div>
</template>

<script>
import { getItemStatusColor } from '@/utils/Constants';
import ClientDeliveryService from '@/services/ClientDeliveryService';
import DeliveryOrderDetailsModal from './DeliveryOrderDetailsModal.vue';

export default {
  name: 'PendingDeliveriesTable',
  components: { DeliveryOrderDetailsModal },
  props: {
    storeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      items: [],
      isLoading: false,
      totalItems: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      showModal: false,
      selectedFolio: null,
      currentStoreId: null,
      headers: [
        {
          text: 'FOLIO', value: 'folio', align: 'start', sortable: true,
        },
        { text: 'CLIENTE', value: 'clientName', sortable: true },
        { text: 'SUCURSAL ENTREGA', value: 'storeName', sortable: false },
        { text: 'VOLUMEN PENDIENTE', value: 'totalItemsPending', sortable: false },
        { text: 'FECHA COMPRA', value: 'purchaseDate', sortable: true },
        { text: 'ESTATUS', value: 'statusDescription', sortable: false },
        {
          text: '', value: 'actions', sortable: false, align: 'end',
        },
      ],
    };
  },
  watch: {
    tableOptions: {
      handler(newVal) {
        const limit = newVal.itemsPerPage > 0 ? newVal.itemsPerPage : 1000;
        const offset = (newVal.page - 1) * limit;

        // Cuando cambies el combo de "5" a "10", esto le avisa al componente Padre
        this.$emit('pagination', { limit, offset });
      },
      deep: true,
    },
  },
  methods: {
    getItemStatusColor,
    openDetailModal(item) {
      this.selectedFolio = item.folio;
      // Toma la sucursal de las props o del usuario en sesión
      this.currentStoreId = this.$store.getters.currentStoreId;
      this.showModal = true;
    },
    openCollectionTicketPreview(item) {
      const routeData = this.$router.resolve({
        name: 'resguardo-vale-ticket',
        params: { folio: String(item.folio), mode: 'todas' },
      });

      const openedTab = window.open(routeData.href, '_blank');

      if (!openedTab) {
        this.$router.push({
          name: 'resguardo-vale-ticket',
          params: { folio: String(item.folio) },
        });
      }
    },
    onOptionsUpdate(options) {
      const { page, itemsPerPage } = options;
      // Si eligen "Todos", mandamos un límite muy alto para proteger el backend
      const limit = itemsPerPage > 0 ? itemsPerPage : 1000;
      const offset = (page - 1) * limit;

      // Avisamos hacia arriba que la paginación cambió
      this.$emit('pagination', { limit, offset });
    },
    async loadData(params) {
      this.tableOptions.itemsPerPage = params.limit;
      this.tableOptions.page = Math.floor(params.offset / params.limit) + 1;

      this.isLoading = true;
      try {
        const apiParams = {
          store_id: params.store_id,
          search: params.search,
          start_date: params.start_date,
          end_date: params.end_date,
          status_id: params.status_id,
          limit: params.limit,
          offset: params.offset,
        };

        const response = await ClientDeliveryService.getPendingDeliveries(apiParams);
        if (response.success) {
          this.items = response.data || [];
          this.totalItems = response.totalRecords || this.items.length;
          const readyCount = this.items.filter((item) => item.statusId === 4).length;
          this.$emit('update-count', readyCount);
        }
      } catch (error) {
        this.$emit('error', 'No se pudieron obtener los pendientes.');
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    goToDispatch(item) {
      this.$router.push({
        name: 'order-dispatch',
        params: { folio: item.folio },
        query: {
          store_id: item.storeId,
          status_id: item.statusId,
          status_desc: item.statusDescription,
        },
      });
    },
  },
};
</script>
<style scoped>
.cursor-pointer { cursor: pointer; }
</style>
